import { supabase } from '../lib/supabase';

export const stripeService = {
  /**
   * Create a Stripe Checkout session and return the redirect URL.
   * @param {string} companyId
   * @param {number} siteCount
   * @param {string} billingCycle - 'monthly' | 'annual'
   * @param {string|null} returnUrl
   */
  async createCheckoutSession(companyId, siteCount, billingCycle, returnUrl = null) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      // Use fetch directly so we can read the JSON body on non-2xx responses.
      // supabase.functions.invoke discards the body when status >= 400 and only
      // returns the generic "Edge Function returned a non-2xx status code" message.
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const payload = {
        siteCount,
        billingCycle,
        companyId,
        ...(returnUrl ? { returnUrl } : {}),
      };

      const response = await fetch(
        `${supabaseUrl}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'apikey': anonKey,
          },
          body: JSON.stringify(payload),
        }
      );

      let json;
      try { json = await response?.json(); } catch (_) { json = {}; }

      if (!response?.ok) {
        const keyInfo = json?.stripeKeyMode
          ? ` [Key mode: ${json?.stripeKeyMode}, prefix: ${json?.stripeKeyPrefix}]`
          : '';
        // Status-aware copy: 429 means retry shortly, 5xx means transient
        // server-side issue, 4xx (other) means the request was rejected.
        // Existing JSON-body message wins when present so server-supplied
        // detail isn't lost.
        let friendly;
        if (response.status === 429) {
          friendly = json?.error || 'Service is busy, please retry in a moment.';
        } else if (response.status >= 500) {
          friendly = json?.error || 'Service temporarily unavailable, please retry shortly.';
        } else {
          friendly = json?.error || `Checkout failed (HTTP ${response.status})`;
        }
        const e = new Error(friendly + keyInfo);
        e.status = response.status;
        throw e;
      }
      if (json?.error) throw new Error(json.error);

      return { data: json, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message, status: err?.status } };
    }
  },

  /**
   * Update an existing subscription to a new site count.
   * Calls the update-subscription edge function.
   * @param {string} companyId
   * @param {number} siteCount
   */
  async updateSubscription(companyId, siteCount) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/update-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'apikey': anonKey,
          },
          body: JSON.stringify({ companyId, siteCount }),
        }
      );

      let json;
      try { json = await response?.json(); } catch (_) { json = {}; }

      if (!response?.ok) {
        let friendly;
        if (response.status === 429) {
          friendly = json?.error || 'Service is busy, please retry in a moment.';
        } else if (response.status >= 500) {
          friendly = json?.error || 'Service temporarily unavailable, please retry shortly.';
        } else {
          friendly = json?.error || `Update failed (HTTP ${response.status})`;
        }
        const e = new Error(friendly);
        e.status = response.status;
        throw e;
      }
      if (json?.error) throw new Error(json.error);

      return { data: json, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message, status: err?.status } };
    }
  },

  /**
   * Open the Stripe Customer Portal for subscription management.
   */
  async createPortalSession(companyId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const { data, error } = await supabase?.functions?.invoke('create-portal-session', {
        body: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message } };
    }
  },

  /**
   * Manually sync subscription status from Stripe.
   * Reads the live Stripe subscription and writes all fields to the company row.
   * Use this when the webhook may have been missed or the DB shows stale data.
   */
  async syncSubscription(companyId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/sync-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'apikey': anonKey,
          },
          body: JSON.stringify({ companyId }),
        }
      );

      let json;
      try { json = await response?.json(); } catch (_) { json = {}; }

      if (!response?.ok) {
        let friendly;
        if (response.status === 429) {
          friendly = json?.error || 'Service is busy, please retry in a moment.';
        } else if (response.status >= 500) {
          friendly = json?.error || 'Service temporarily unavailable, please retry shortly.';
        } else {
          friendly = json?.error || `Sync failed (HTTP ${response.status})`;
        }
        const e = new Error(friendly);
        e.status = response.status;
        throw e;
      }
      if (json?.error) throw new Error(json.error);

      return { data: json, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message, status: err?.status } };
    }
  },

  /**
   * Fetch invoice history from Stripe via edge function.
   */
  async getInvoices() {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const { data, error } = await supabase?.functions?.invoke('get-invoices', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return { data: data?.invoices || [], error: null };
    } catch (err) {
      return { data: [], error: { message: err?.message } };
    }
  },

  getStatusBadge(status) {
    const badges = {
      active:   { label: 'Active',   className: 'bg-green-100 text-green-700 border-green-200' },
      trialing: { label: 'Trial',    className: 'bg-blue-100 text-blue-700 border-blue-200' },
      past_due: { label: 'Past Due', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
      canceled: { label: 'Canceled', className: 'bg-red-100 text-red-700 border-red-200' },
      unpaid:   { label: 'Unpaid',   className: 'bg-red-100 text-red-700 border-red-200' },
      // 'paused' only fires via trial_settings.end_behavior.missing_payment_method=pause
      // (see create-checkout-session/index.ts), so it's always the trial-ended scenario.
      paused:   { label: 'Trial Ended', className: 'bg-amber-100 text-amber-700 border-amber-200' },
      inactive: { label: 'Inactive', className: 'bg-gray-100 text-gray-600 border-gray-200' },
    };
    return badges?.[status] || badges?.inactive;
  },
};
